import React, {useState} from "react";
import {useObserver} from "mobx-react-lite";
import {Row, Col, Select, DatePicker, Collapse, Button, PageHeader, message} from "antd";
import {PlusOutlined, DownloadOutlined} from "@ant-design/icons";
import {useFetch} from "../../../hooks/useFetch";
import stores from "../../../store/stores";
import {CreateCourierRequestModal} from "../modals/CreateCourierRequestModal";
import {UpdateCourierRequestModal} from "../modals/UpdateCourierRequestModal";

import {arrayAlphabeticalSort, b64DecodeToUrl} from "utils/helpers";

import {
    CompanyFilter,
    CourierRequestStatusFilter,
    DynamicCityFilter,
    DynamicDistrictFilter,
    SubCompanyFilter,
    WeekFilter,
    WorkingModelTypeFilter
} from "components/_defaultComponents/FilterComponents";
import moment from "moment";
import locale from "antd/es/date-picker/locale/tr_TR";

import {CourierRequestPrioritySelect} from "../components/courierRequestPrioritySelect/CourierRequestPrioritySelect";
import {CourierRequestsTable} from "./courierRequestsTable/CourierRequestsTable";

const CourierRequestsPage = ({isActive}) => {
    const [recallRequest, setRecallRequest] = useState(false);

    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedCourierRequest, setSelectedCourierRequest] = useState(undefined);
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [closeReason, setCloseReason] = useState("");

    const defaultFilters = {
        weekNumber: moment().week(),
        startDate: undefined,
        endDate: undefined,
        zoneManagementId: undefined,
        zoneId: undefined,
        cityId: undefined,
        districtIds: undefined,
        companyId: undefined,
        subCompanyId: undefined,
        workingModelId: undefined,
        status: undefined,
        sortBy: "createdAt",
        sortOrder: "desc",
        isDeleted: false,
        paginate: true,
        skip: 0,
        limit: 10
    };
    const [filters, setFilters] = useState({
        ...defaultFilters
    });
    // Zone Management listesi
    const [zoneManagementLoading, zoneManagementData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getZoneManagementList.bind(null, {
            skip: 0,
            limit: 1000,
            paginate: false
        }),
        condition: isActive,
        dependencies: [isActive]
    });

    const [courierRequestLoading, courierRequestData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierRequestStore.getCourierRequestList.bind(null, {
            ...filters,
            skip: filters.skip,
            limit: filters.limit
        }),
        condition: isActive,
        dependencies: [recallRequest, JSON.stringify(filters), isActive],
        waitForResponse: true
    });

    const handleUpdateFilters = filters => {
        setFilters(prev => ({
            ...prev,
            ...filters,
            skip: defaultFilters.skip,
            limit: defaultFilters.limit
        }));
    };

    const handleUpdatePagination = pagination => {
        setFilters(prev => ({
            ...prev,
            skip: pagination.skip,
            limit: pagination.limit
        }));
    };

    // Zone İşlemleri
    const handleCreateZone = () => {
        setCreateModalVisible(true);
    };

    const handleEditCourierRequest = record => {
        setSelectedCourierRequest(record);
        setEditModalVisible(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalVisible(false);
    };

    const handleCloseEditModal = () => {
        setEditModalVisible(false);
        setSelectedCourierRequest(null);
    };

    const handleDeleteCourierRequest = async record => {
        const result = await stores.courierRequestStore.deleteCourierRequest({
            id: record._id
        });
        if (result) {
            setRecallRequest(!recallRequest);
        }
    };

    const handleCloseCourierRequest = async ({record, reason}) => {
        const result = await stores.courierRequestStore.closeCourierRequest({
            id: record._id,
            reason
        });
        if (result) {
            setRecallRequest(!recallRequest);
        }
        setCloseReason("");
    };

    const handleWeekNumberFilterChange = value => {
        handleUpdateFilters({
            ...filters,
            weekNumber: value,
            startDate: undefined,
            endDate: undefined
        });
    };

    const handleDateRangeFilterChange = value => {
        handleUpdateFilters({
            ...filters,
            startDate: value?.[0],
            endDate: value?.[1],
            weekNumber: undefined
        });
    };

    const handleZoneManagementFilterChange = value => {
        handleUpdateFilters({
            ...filters,
            zoneManagementId: value
        });
    };

    const handleCityFilterChange = value => {
        handleUpdateFilters({
            ...filters,
            cityId: value,
            districtIds: []
        });
    };

    const handleDistrictFilterChange = value => {
        handleUpdateFilters({
            ...filters,
            districtIds: value
        });
    };

    const handleDownloadExcel = async () => {
        const messageKey = "courier-requests-excel";
        message.loading({content: "Excel hazırlanıyor...", key: messageKey});

        setLoadingExcelData(true);
        try {
            const response = await stores.courierRequestStore.exportCourierRequestsList({
                ...filters
            });

            const base64Data = response?.base64Excel;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute(
                "download",
                `kurye-talepleri-${moment().format("YYYY-MM-DD-HH-mm-ss")}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success({
                content: "Excel dosyası başarıyla indirildi.",
                key: messageKey,
                duration: 4
            });
        } catch (error) {
            console.log("error", error);
            message.error({
                content: "Excel dosyası indirilirken bir hata oluştu.",
                key: messageKey,
                duration: 4
            });
        } finally {
            setLoadingExcelData(false);
        }
    };

    return useObserver(() => (
        <React.Fragment>
            <PageHeader title="Kurye Talepleri" style={{paddingLeft: "0px"}} />
            <CreateCourierRequestModal
                visibility={createModalVisible}
                onCancel={handleCloseCreateModal}
                callbackFunc={() => setRecallRequest(!recallRequest)}
            />

            <UpdateCourierRequestModal
                visibility={editModalVisible}
                detail={selectedCourierRequest}
                onCancel={handleCloseEditModal}
                callbackFunc={() => setRecallRequest(!recallRequest)}
            />

            <Collapse style={{marginBottom: "8px"}}>
                <Collapse.Panel
                    header="Filtreler"
                    key="filters"
                    extra={
                        <Row style={{justifyContent: "flex-end"}} gutter={[8, 8]}>
                            {stores.meStore.checkRole("exportCourierRequestsList") &&
                                stores.meStore.checkPermission("courierRequests.export") && (
                                    <Col>
                                        <Button
                                            disabled={loadingExcelData}
                                            loading={loadingExcelData}
                                            size="small"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleDownloadExcel();
                                            }}
                                            icon={<DownloadOutlined />}
                                            type="primary"
                                        >
                                            Excel Çıktısı İndir
                                        </Button>
                                    </Col>
                                )}
                            {stores.meStore.checkRole("createCourierRequest") &&
                                stores.meStore.checkPermission("courierRequests.requestActions") && (
                                    <Col>
                                        <Button
                                            size="small"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleCreateZone();
                                            }}
                                            icon={<PlusOutlined />}
                                            type="primary"
                                        >
                                            Yeni Kurye Talebi
                                        </Button>
                                    </Col>
                                )}
                        </Row>
                    }
                >
                    <Row type="flex" justify="flex-start" gutter={[8, 8]}>
                        <Col>
                            <WeekFilter
                                value={filters.weekNumber}
                                onChange={handleWeekNumberFilterChange}
                                onClear={handleWeekNumberFilterChange}
                                allowClear
                                hideTitle={true}
                                selectMarginBottom="0px"
                                placeholder="Hafta Seç"
                                mode="single"
                                style={{minWidth: "150px", maxWidth: "250px"}}
                            />
                        </Col>
                        <Col>
                            <DatePicker.RangePicker
                                locale={locale}
                                placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                                ranges={{
                                    Bugün: [moment(), moment()],
                                    "Bu Hafta": [moment().startOf("week"), moment().endOf("week")],
                                    "Bu Ay": [moment().startOf("month"), moment().endOf("month")]
                                }}
                                onChange={handleDateRangeFilterChange}
                                value={[filters.startDate, filters.endDate]}
                                defaultValue={[filters.startDate, filters.endDate]}
                                format={"DD.MM.YYYY"}
                            />
                        </Col>
                        {stores.meStore.checkRole("courierRequests.showZoneManagmentFilter") && (
                            <Col>
                                <Select
                                    placeholder="Bölge Müdürlüğü Seç"
                                    style={{width: 200}}
                                    allowClear
                                    value={filters.zoneManagementId}
                                    onChange={handleZoneManagementFilterChange}
                                    loading={zoneManagementLoading}
                                >
                                    {Array.isArray(zoneManagementData?.data) &&
                                        arrayAlphabeticalSort(
                                            zoneManagementData?.data || [],
                                            "name"
                                        ).map(item => (
                                            <Select.Option key={item._id} value={item._id}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Col>
                        )}
                        <Col>
                            <DynamicCityFilter
                                value={filters.cityId}
                                onChange={handleCityFilterChange}
                                onClear={handleCityFilterChange}
                                allowClear
                                showLabel={false}
                                selectMarginBottom="0px"
                                placeholder="Şehir Seç"
                                style={{minWidth: "150px"}}
                            />
                        </Col>
                        <Col>
                            <DynamicDistrictFilter
                                value={filters.districtIds}
                                cityId={filters.cityId}
                                onChange={handleDistrictFilterChange}
                                onClear={handleDistrictFilterChange}
                                allowClear
                                showLabel={false}
                                selectMarginBottom="0px"
                                placeholder="İlçe Seç"
                                mode="multiple"
                                style={{minWidth: "150px", maxWidth: "250px"}}
                            />
                        </Col>
                        <Col>
                            <CompanyFilter
                                showAllOption={false}
                                showPoolOption={false}
                                value={filters.companyId}
                                onChange={value =>
                                    handleUpdateFilters({
                                        ...filters,
                                        companyId: value,
                                        subcompanyId: undefined
                                    })
                                }
                                allowClear={true}
                                style={{minWidth: "150px", maxWidth: "250px"}}
                                hideTitle={true}
                                selectMarginBottom="0px"
                                placeholder="Firma Seç"
                            />
                        </Col>
                        <Col>
                            <SubCompanyFilter
                                value={filters.subCompanyId}
                                onChange={value =>
                                    handleUpdateFilters({...filters, subCompanyId: value})
                                }
                                company={filters.companyId}
                                allowClear={true}
                                showAllOption={false}
                                showSearch={true}
                                hideTitle={true}
                                selectMarginBottom="0px"
                                placeholder="Alt Firma Seç"
                                style={{minWidth: "150px", maxWidth: "250px"}}
                            />
                        </Col>
                        <Col>
                            <WorkingModelTypeFilter
                                disabled={false}
                                value={filters.workingModelId}
                                onChange={value =>
                                    handleUpdateFilters({...filters, workingModelId: value})
                                }
                                allowClear={true}
                                showAllOption={false}
                                showLabel={false}
                                marginBottom="0px"
                                placeholder="Çalışma Modeli Seç"
                                style={{minWidth: "150px", maxWidth: "250px"}}
                            />
                        </Col>
                        <Col>
                            <CourierRequestPrioritySelect
                                value={filters.priority}
                                onChange={value =>
                                    handleUpdateFilters({...filters, priority: value})
                                }
                                allowClear={true}
                                showAllOption={false}
                                showTitle={false}
                                marginBottom="0px"
                                placeholder="Öncelik Seç"
                                style={{minWidth: "150px", maxWidth: "250px"}}
                            />
                        </Col>
                        <Col>
                            <CourierRequestStatusFilter
                                value={filters.status}
                                onChange={value => handleUpdateFilters({...filters, status: value})}
                                allowClear={true}
                                showLabel={false}
                                marginBottom="0px"
                                placeholder="Durum Seç"
                                style={{minWidth: "150px", maxWidth: "250px"}}
                            />
                        </Col>
                    </Row>
                </Collapse.Panel>
            </Collapse>
            <CourierRequestsTable
                courierRequestLoading={courierRequestLoading}
                courierRequestData={courierRequestData}
                handleEditCourierRequest={handleEditCourierRequest}
                handleDeleteCourierRequest={handleDeleteCourierRequest}
                handleCloseCourierRequest={handleCloseCourierRequest}
                closeReason={closeReason}
                setCloseReason={setCloseReason}
                pagination={{
                    limit: filters.limit,
                    skip: filters.skip
                }}
                setPagination={handleUpdatePagination}
            />
        </React.Fragment>
    ));
};

export default CourierRequestsPage;
